import { useState, type FormEvent } from 'react'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Button } from '../../platform/primitives/Button'
import { GoogleG } from '../../platform/primitives/GoogleG'
import { Field } from '../../platform/primitives/Field'
import { useIsCoarsePointer } from '../../platform/responsive/useBreakpoint'
import { emit } from '../../platform/analytics/analytics'
import { authErrorFor } from '../../auth/errors'
import { AuthFailure } from '../../auth/port'
import { useAuth } from '../../auth/useAuth'
import type { SignInOutcome } from '../../auth/AuthProvider'
import type { FieldError } from '../../auth/errors'
import { AuthCard, AuthDivider } from './AuthCard'
import { FormError } from '../../auth/FormError'
import './auth.css'

/**
 * M1-R1: create an account with email and password, or with Google.
 *
 * Deliberately a sibling of `SignIn` rather than a mode of it. The two screens
 * share a shell and an error rule and nothing else: this one sends a
 * verification email, states a password rule up front, and has no reset link —
 * an account that does not exist yet cannot have a forgotten password.
 *
 * **There is no `signup_failed` event**, and that is the catalogue's decision
 * rather than an omission here: `EVENT_NAMES` registers `signin_failed` and no
 * counterpart. A failure is rendered on its field and not measured. Adding the
 * name is a one-line change in `events.ts` when somebody wants the funnel; it
 * is not something this screen may invent, because `emit()` throws in dev on an
 * unregistered name and the crash would land on the failure screen.
 *
 * **`signup_completed` is emitted for email and NOT for Google**, deliberately,
 * and the funnel is lossy because of it. Saying so here because the first
 * version claimed otherwise in a comment.
 *
 * Google sign-up and Google sign-in are one Firebase operation. This screen
 * cannot tell a new account from a returning one, so both of the obvious
 * spellings report something false:
 *
 *   - the POPUP arm resolving means a session exists — but for a user who
 *     already had an account, and `signup_completed` for them is an invented
 *     signup.
 *   - the REDIRECT arm cannot report at all: the document is replaced, and the
 *     provider's completion on return is `signin_completed` (AuthProvider.tsx),
 *     not this event. So `signup_started → signup_completed` would read ~0% for
 *     every coarse-pointer user — every phone, on the screen whose audience is
 *     most mobile.
 *
 * The rule that survives both: report `signup_completed` only where the client
 * KNOWS an account was created, which is `createUserWithEmailAndPassword`
 * resolving. `signup_started` still carries `method: 'google'`, so the intent
 * is measured and the completion is honestly absent rather than wrong. Doing it
 * properly needs `getAdditionalUserInfo(result).isNewUser` carried across the
 * port from the adapter — a port change, tracked as a follow-up.
 */
export function SignUp() {
  const auth = useAuth()
  const coarse = useIsCoarsePointer()
  const [error, setError] = useState<FieldError | null>(null)
  const [busy, setBusy] = useState(false)

  // Same two sources as `SignIn`, resolved during render for the same reason: a
  // social redirect fails while this screen does not exist, so the provider
  // holds that failure and this screen finds it already there on mount.
  const shown = error ?? auth.redirectError

  async function run(method: 'email' | 'google', action: () => Promise<SignInOutcome | void>) {
    setError(null)
    auth.clearRedirectError()
    setBusy(true)
    emit('signup_started', { method })
    try {
      await action()
      // Only the email path, and only because it is the only one that resolves
      // *because an account was created*. See the header for why neither Google
      // arm can say the same thing without saying something false.
      if (method === 'email') emit('signup_completed', { method })
    } catch (thrown) {
      // Nothing types what comes through the port, so an unmapped throw gets
      // the unclassified message rather than a guessed field.
      const detail = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      setError(detail)
    } finally {
      setBusy(false)
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const email = String(form.get('email') ?? '')
    const password = String(form.get('password') ?? '')
    void run('email', async () => {
      await auth.signUp(email, password)
      // Fired here and deliberately NOT awaited into the failure path above:
      // the account now exists. A user who is signed up but sees "sign-up
      // failed" because a follow-up email bounced is worse off than one who
      // lands on Overview with the verification banner and a Resend button.
      //
      // The rejection is swallowed rather than reported anywhere, which is a
      // real cost: a Firebase throttle on this call is invisible. It is the
      // right trade because the banner's own Resend reports
      // `auth/too-many-requests` distinguishably, so the user meets the
      // failure at the moment they can act on it instead of at the moment
      // they cannot.
      void auth.sendVerificationEmail().catch(() => {})
    })
  }

  return (
    <AuthCard
      title="Create your household"
      subtitle="Free to start · no card needed"
      foot={<>Already have an account? <InlineLink to="/signin">Sign in</InlineLink></>}
    >
      {/* `noValidate`, so the browser's own bubble does not pre-empt the
          messages M1-R2 asks for. */}
      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <Field
          size="form"
          label="Email"
          name="email"
          type="email"
          autoComplete="username"
          error={shown?.field === 'email' ? shown.message : undefined}
        />
        <Field
          size="form"
          label="Password"
          name="password"
          type="password"
          // `new-password`, not `current-password`: it is what tells a password
          // manager to offer to generate one rather than to fill the account
          // the user is trying to leave behind.
          autoComplete="new-password"
          // Firebase's own minimum, stated before the round trip that would
          // otherwise teach it — `auth/weak-password` is a rule we already know
          // being learned from a server.
          hint="At least 6 characters."
          error={shown?.field === 'password' ? shown.message : undefined}
        />
        <FormError error={shown} />
        <Button type="submit" variant="primary" size="form" disabled={busy}>
          {busy ? 'Creating account…' : 'Create account'}
        </Button>
        {/**
         * The consent line — inside the card, under the submit, where the
         * design puts it.
         *
         * **Bare `<a>`, deliberately, and NOT `InlineLink`.** These are links
         * whose position is determined by the flow of the sentence around
         * them, which is CLAUDE.md's WCAG 2.5.8 exemption and what
         * `InlineLink`'s own docstring means by *"Not for a link inside a
         * sentence."* The first version used `InlineLink` and the browser
         * showed why the exemption exists: its `inline-flex` box takes
         * `min-width`/`min-height` from `useHitTarget`, which is 44px on a
         * coarse pointer, so a five-character "Terms" became a 44x44 centred
         * box mid-sentence and the paragraph broke across three lines at
         * 375px. `reset.css` leaves the UA underline in place, so these still
         * carry more than colour — rule 11.
         *
         * **External.** They point at the marketing site, which publishes the
         * current Terms and Privacy policy with their version and date. The
         * in-app `/legal/privacy` and `/legal/terms` pages only name the
         * document and link to the same place, so linking there directly saves
         * a hop.
         *
         * **A new tab, which is a decision rather than a habit.** This form is
         * half-filled by the time anyone reads the consent line — an email and
         * a password that is not in React state and cannot be restored — so a
         * same-tab navigation to the marketing site discards it. Announced, so
         * the new tab is not a surprise to a screen-reader user.
         */}
        <p className="auth-consent">
          By continuing you agree to our{' '}
          <a href="https://tracke5.com/terms.html" target="_blank" rel="noopener noreferrer">
            Terms<span className="sr-only"> (opens in a new tab)</span>
          </a>{' '}
          and{' '}
          <a href="https://tracke5.com/privacy.html" target="_blank" rel="noopener noreferrer">
            Privacy policy<span className="sr-only"> (opens in a new tab)</span>
          </a>
          .
        </p>
      </form>

      <AuthDivider />

      {/* `outline`, not a second `primary`: one lime surface per screen, and
          the email submit is it. */}
      <Button
        variant="outline"
        size="form"
        className="btn--google"
        disabled={busy}
        // M1-R23, the same rule as sign-in: popups are unreliable on mobile
        // browsers. `useIsCoarsePointer` is the single source.
        onClick={() =>
          void run('google', () => auth.signInWithGoogle(coarse ? 'redirect' : 'popup'))
        }
      >
        <>
          <GoogleG />
          {busy ? 'Opening Google…' : 'Continue with Google'}
        </>
      </Button>

      {/* Stated rather than silently missing, exactly as on sign-in. Apple
          needs C8, and an Apple-only iOS user who tries to create a second
          account here would end up with two. */}
      <p className="auth-card__note">
        Sign up with Apple isn't available on the web yet. If that's how you signed up on your
        iPhone, use the app for now.
      </p>

    </AuthCard>
  )
}
